import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomTable from "../../../../../components/custom-table/custom-table";
import ImageviewerModal from "../../../../../components/modals/imageViewerModal/imageviewerModal";
import ImageViewerWithBadgesModal from "../../../../../components/modals/imageViewerWithBadgesModal/imageViewerWithBadgesModal";
import {
  FilterSection,
  FilterValues,
} from "../../../../../data-model/filterValues";
import { ScreenNamesForBigQuery } from "../../../../../enums/eventsData";
import { ModalId } from "../../../../../enums/modalId";
import { TableNames } from "../../../../../enums/tableNames";
import { setFilter } from "../../../../../redux/slice/filterValuesSlice";
import { closeModal as closeReduxModal } from "../../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import { swapUnderscoreHyphen } from "../../../../../utils/swapUnderscoreWithHypen";
import { useFilterOptionsGeneratorFromAlgolia } from "../../../hooks/useFilterOptionsGeneratorFromAlgolia";
import { useFilterAndSearch } from "../../hooks/useFilterAndSearch";
import { useInitialDataFetchingForTable } from "../../hooks/useInitialDataFetchingForTable";
import { useModalOpenerForMyPaOrders } from "../../hooks/useModalOpenerForMyPaOrders";
import { useMyPaOrdersTableData } from "../../hooks/useMyPaOrdersTableData";
import { useNextPageDataFetch } from "../../hooks/useNextPageDataFetch";
import { FILTER_CREATION_CONFIG_FOR_MY_PA_ORDERS } from "../../utils/filterConfigForMyPaOrders";
import { MyPaOrdersTableColumns } from "../../utils/generateTableDataFromAlgoliaRecords";
import { handleDocUploadScreenshotViewForMyPaOrders } from "../../utils/handleDocUploadScreenshot";
import { handleNavigateToNextScreenFromMyPaTable } from "../../utils/handleNavigateToNextPageFromMyMedicalPaTable";
import { handleRpaStatusScreenshotViewForMyPaOrders } from "../../utils/handleRpaStatusScreenshot";

const MyMedicalPaOrdersTableFromAlgolia = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const navigate = useNavigate();
  const {
    myMedicalPaOrdersState,
    setMyMedicalPaOrdersState,
    tableData,
    setTableData,
  } = useMyPaOrdersTableData();
  const { initialDataFetchingExecuted, setInitialDataFetchingExecuted } =
    useInitialDataFetchingForTable(setMyMedicalPaOrdersState, setTableData);
  const { paginationResetCount, setSearchTerm, searchTerm, validFilters } =
    useFilterAndSearch(
      initialDataFetchingExecuted,
      setTableData,
      setMyMedicalPaOrdersState,
    );

  const { setEndIndex } = useNextPageDataFetch(
    validFilters,
    initialDataFetchingExecuted,
    searchTerm ?? "",
    myMedicalPaOrdersState,
    setMyMedicalPaOrdersState,
    setTableData,
  );

  const { openedModalId, metaData } = useModalOpenerForMyPaOrders();

  const { filterData, algoliaMultiFacetsCountLoading } =
    useFilterOptionsGeneratorFromAlgolia(
      FILTER_CREATION_CONFIG_FOR_MY_PA_ORDERS,
    );

  return (
    <>
      <div className="h-full">
        <CustomTable
          tableHeaders={MyPaOrdersTableColumns}
          tableData={tableData}
          tableName={TableNames.MY_PA_ORDERS_FROM_ALGOLIA}
          itemsPerPage={10}
          pagesPerView={4}
          count={tableData?.length ?? 0}
          totalCount={myMedicalPaOrdersState?.data?.nb_hits ?? 0}
          endIndexOfTable={setEndIndex}
          searchingText={(searchText: string) => {
            setSearchTerm(searchText);
          }}
          hideSearchBar={false}
          showInLineLoader={myMedicalPaOrdersState.showinLineLoader}
          shouldLogEvent={true}
          screenName={ScreenNamesForBigQuery.WORKLIST}
          isFetching={myMedicalPaOrdersState.loading}
          requestForPaginationReset={paginationResetCount}
          filterSectionsData={filterData as FilterSection[]}
          showFilterButton={
            !myMedicalPaOrdersState.loading && !algoliaMultiFacetsCountLoading
          }
          onFilterApplyClicked={(data: FilterValues) => {
            dispatch(setFilter(data));
          }}
          onRefreshButton={() => {
            setInitialDataFetchingExecuted(false);
          }}
          showRefreshButton={true}
          isDisabled={
            myMedicalPaOrdersState.loading ||
            myMedicalPaOrdersState.showinLineLoader
          }
          onRowClick={(rowData: any) => {
            handleNavigateToNextScreenFromMyPaTable(
              rowData,
              user?.id ?? "",
              user?.email ?? "",
              swapUnderscoreHyphen("my-pa-orders"),
              navigate,
            );
          }}
          onPropagateEventOnClickInRpaStatusCell={(id: string) => {
            handleRpaStatusScreenshotViewForMyPaOrders(
              id,
              dispatch,
              authStatusOptions,
            );
          }}
          onPropagateEventOnClickInRpaStatusTwoCell={(id: string) => {
            handleDocUploadScreenshotViewForMyPaOrders(
              id,
              dispatch,
              authStatusOptions,
            );
          }}
        />
      </div>
      {openedModalId === ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL && (
        <ImageViewerWithBadgesModal
          tabs={metaData?.tabs ?? []}
          showLoading={metaData?.showLoading ?? false}
          title="Write Back Trail"
        />
      )}
      {openedModalId === ModalId.IMAGE_VIEWER_MODAL && (
        <ImageviewerModal
          fileUrls={metaData?.fileUrls ?? []}
          filePaths={metaData?.filePaths ?? []}
          onClose={() => dispatch(closeReduxModal())}
          showLoader={metaData?.showLoader ?? false}
        />
      )}
    </>
  );
};

export default MyMedicalPaOrdersTableFromAlgolia;
